import { useState } from 'react';

type VDRLog = {
  id: string;
  visitor_email: string | null;
  visitor_name?: string | null;
  visitor_phone?: string | null;
  visitor_id: string;
  action_type: string;
  created_at: string;
  path: string;
  metadata: any;
  country?: string;
  city?: string;
  device_info?: any;
  user_agent?: string;
};

type DeviceInfo = {
  platform?: string;
  browser?: string;
  screenSize?: string;
  timezone?: string;
  userAgent?: string;
};

type UserHistoryModalProps = {
  user: {
    id: string;
    email: string | null;
    name: string | null;
    phone: string | null;
    visitorId: string;
    lastActive: string;
    firstSeen: string;
    location: string;
    country?: string;
    totalTime: number;
    visitCount: number;
    devices: DeviceInfo[];
  };
  logs: VDRLog[];
  onClose: () => void;
};

export default function UserHistoryModal({ user, logs, onClose }: UserHistoryModalProps) {
  const [activeTab, setActiveTab] = useState<'timeline' | 'pages' | 'devices'>('timeline');

  // Calculate Stats
  const totalPageViews = logs.filter(l => l.action_type === 'view_page').length;
  const totalClicks = logs.filter(l => l.action_type === 'click_button' || l.action_type === 'click_link').length;
  const totalDownloads = logs.filter(l => l.action_type === 'download_file').length;
  
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    if (m < 60) return `${m}m ${Math.round(seconds % 60)}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(dateStr);
  };

  // Group logs by date
  const groupedLogs = logs.reduce((acc, log) => {
    const date = new Date(log.created_at).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, VDRLog[]>);

  // Get unique pages visited
  const pagesVisited = logs
    .filter(l => l.action_type === 'view_page')
    .reduce((acc, log) => {
      const path = log.path || '/';
      if (!acc[path]) acc[path] = { count: 0, lastVisit: log.created_at };
      acc[path].count++;
      if (new Date(log.created_at) > new Date(acc[path].lastVisit)) {
        acc[path].lastVisit = log.created_at;
      }
      return acc;
    }, {} as Record<string, { count: number; lastVisit: string }>);

  const getActionIcon = (type: string) => {
    const icons: Record<string, string> = {
      'view_page': '📄',
      'click_button': '👆',
      'click_link': '🔗',
      'download_file': '📥',
      'auth_success': '✅',
      'time_on_page': '⏱️',
      'scroll_depth': '📜',
      'request_access': '🔑',
    };
    return icons[type] || '•';
  };

  const getActionLabel = (type: string) => {
    const labels: Record<string, string> = {
      'view_page': 'Viewed page',
      'click_button': 'Clicked button',
      'click_link': 'Clicked link',
      'download_file': 'Downloaded file',
      'auth_success': 'Authenticated',
      'time_on_page': 'Time on page',
      'scroll_depth': 'Scrolled',
      'request_access': 'Requested access',
    };
    return labels[type] || type.replace(/_/g, ' ');
  };

  const getDeviceIcon = (platform?: string) => {
    if (!platform) return '💻';
    const p = platform.toLowerCase();
    if (p.includes('iphone') || p.includes('android')) return '📱';
    if (p.includes('ipad') || p.includes('tablet')) return '📱';
    if (p.includes('mac')) return '💻';
    if (p.includes('win')) return '🖥️';
    if (p.includes('linux')) return '🐧';
    return '💻';
  };

  const getBrowserIcon = (browser?: string) => {
    if (!browser) return '🌐';
    const b = browser.toLowerCase();
    if (b.includes('chrome')) return '🔵';
    if (b.includes('safari')) return '🧭';
    if (b.includes('firefox')) return '🦊';
    if (b.includes('edge')) return '🔷';
    return '🌐';
  };

  // Get initials for avatar
  const getInitials = () => {
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '?';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-6 px-4" onClick={onClose}>
      <div 
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-neutral-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-neutral-800 to-neutral-600 flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                {getInitials()}
              </div>
              
              {/* Info */}
              <div>
                <h2 className="text-xl font-semibold text-neutral-900">
                  {user.name || user.email || 'Anonymous Visitor'}
                </h2>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-neutral-500">
                  {user.email && (
                    <span className="flex items-center gap-1">
                      <span className="text-neutral-400">✉️</span>
                      {user.email}
                    </span>
                  )}
                  {user.phone && (
                    <span className="flex items-center gap-1">
                      <span className="text-neutral-400">📞</span>
                      {user.phone}
                    </span>
                  )}
                  {user.location && user.location !== 'Unknown' && (
                    <span className="flex items-center gap-1">
                      <span className="text-neutral-400">📍</span>
                      {user.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <button 
              onClick={onClose} 
              className="text-neutral-400 hover:text-neutral-900 p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-neutral-50 border-b border-neutral-100">
          <div className="bg-white rounded-xl p-3 border border-neutral-100">
            <div className="text-[11px] text-neutral-500 uppercase tracking-wide font-medium">Time Spent</div>
            <div className="text-xl font-semibold mt-0.5 text-neutral-900">{formatDuration(user.totalTime)}</div>
          </div>
          <div className="bg-white rounded-xl p-3 border border-neutral-100">
            <div className="text-[11px] text-neutral-500 uppercase tracking-wide font-medium">Page Views</div>
            <div className="text-xl font-semibold mt-0.5 text-neutral-900">{totalPageViews}</div>
          </div>
          <div className="bg-white rounded-xl p-3 border border-neutral-100">
            <div className="text-[11px] text-neutral-500 uppercase tracking-wide font-medium">Interactions</div>
            <div className="text-xl font-semibold mt-0.5 text-neutral-900">{totalClicks}</div>
          </div>
          <div className="bg-white rounded-xl p-3 border border-neutral-100">
            <div className="text-[11px] text-neutral-500 uppercase tracking-wide font-medium">Downloads</div>
            <div className="text-xl font-semibold mt-0.5 text-neutral-900">{totalDownloads}</div>
          </div>
        </div>

        {/* Session Info */}
        <div className="flex items-center gap-6 px-6 py-3 text-xs text-neutral-500 border-b border-neutral-100 bg-neutral-50/50">
          <span>
            <span className="font-medium text-neutral-700">First seen:</span> {formatDate(user.firstSeen)}
          </span>
          <span>
            <span className="font-medium text-neutral-700">Last active:</span> {formatTimeAgo(user.lastActive)}
          </span>
          <span>
            <span className="font-medium text-neutral-700">Sessions:</span> {user.devices.length || 1}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-4 border-b border-neutral-100">
          {[
            { id: 'timeline', label: 'Activity', icon: '📋' },
            { id: 'pages', label: 'Pages', icon: '📄' },
            { id: 'devices', label: 'Devices', icon: '💻' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white text-neutral-900 border border-neutral-200 border-b-white -mb-px' 
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              <span className="mr-1.5">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto min-h-0 p-4">
          
          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-6">
              {Object.entries(groupedLogs).map(([date, dayLogs]) => (
                <div key={date}>
                  <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3 sticky top-0 bg-white py-1">
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="space-y-2">
                    {dayLogs.map((log) => (
                      <div 
                        key={log.id} 
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-base shrink-0">
                          {getActionIcon(log.action_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-neutral-900 text-sm">
                              {getActionLabel(log.action_type)}
                            </span>
                            <span className="text-xs text-neutral-400 shrink-0">
                              {formatTime(log.created_at)}
                            </span>
                          </div>
                          
                          {log.path && log.action_type === 'view_page' && (
                            <div className="text-xs text-neutral-500 mt-0.5 truncate">
                              {log.path}
                            </div>
                          )}
                          
                          {log.action_type === 'time_on_page' && log.metadata?.duration_ms && (
                            <div className="text-xs text-neutral-500 mt-0.5">
                              Duration: {formatDuration((log.metadata.duration_ms || 0) / 1000)}
                              {log.metadata.scroll_depth && ` • Scrolled ${log.metadata.scroll_depth}%`}
                            </div>
                          )}
                          
                          {(log.action_type === 'click_button' || log.action_type === 'click_link') && log.metadata?.label && (
                            <div className="text-xs text-neutral-500 mt-0.5 truncate">
                              "{log.metadata.label}"
                              {log.metadata.href && <span className="text-neutral-400"> → {log.metadata.href}</span>}
                            </div>
                          )}
                          
                          {log.action_type === 'download_file' && log.metadata?.filename && (
                            <div className="text-xs text-neutral-500 mt-0.5">
                              📎 {log.metadata.filename}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {logs.length === 0 && (
                <div className="text-center text-neutral-400 py-12">
                  No activity recorded yet.
                </div>
              )}
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="space-y-2">
              {Object.entries(pagesVisited)
                .sort((a, b) => b[1].count - a[1].count)
                .map(([path, data]) => (
                  <div 
                    key={path}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-500 shrink-0">
                        📄
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-neutral-900 text-sm truncate">
                          {path === '/' ? 'Home' : path.split('/').pop() || path}
                        </div>
                        <div className="text-xs text-neutral-400 truncate">{path}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="text-sm font-semibold text-neutral-900">{data.count} views</div>
                      <div className="text-xs text-neutral-400">{formatTimeAgo(data.lastVisit)}</div>
                    </div>
                  </div>
                ))}
              
              {Object.keys(pagesVisited).length === 0 && (
                <div className="text-center text-neutral-400 py-12">
                  No pages visited yet.
                </div>
              )}
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <div className="space-y-3">
              {user.devices.length > 0 ? (
                user.devices.map((device, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-xl border border-neutral-100 hover:border-neutral-200 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center text-xl">
                        {getDeviceIcon(device.platform)}
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">
                          {device.platform || 'Unknown Device'}
                        </div>
                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                          <span>{getBrowserIcon(device.browser)} {device.browser || 'Unknown Browser'}</span>
                          {device.screenSize && <span>• {device.screenSize}</span>}
                        </div>
                      </div>
                    </div>
                    
                    {device.timezone && (
                      <div className="text-xs text-neutral-500 flex items-center gap-1.5 mt-2">
                        <span>🕐</span>
                        <span>{device.timezone}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center text-neutral-400 py-12">
                  <div className="text-4xl mb-3">💻</div>
                  <div>No device information available.</div>
                </div>
              )}
              
              {/* Visitor ID */}
              <div className="mt-6 pt-4 border-t border-neutral-100">
                <div className="text-[11px] text-neutral-400 uppercase tracking-wide font-medium mb-1">Visitor ID</div>
                <div className="text-xs font-mono text-neutral-600 bg-neutral-50 px-3 py-2 rounded-lg break-all">
                  {user.visitorId}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-100 bg-neutral-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-xs text-neutral-400">
              {logs.length} total events
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
